import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
} from "../../../components/Buttons/Buttons";
import { Table } from "../../../components/Table/Table";
import { FormItems } from "../../../layout/FormLayout";
import type {
  DataMutate,
  Language,
  OptionType,
  SupportedLanguage,
  TransactionDocument,
} from "../../../types/types";
import styled from "styled-components/macro";
import { rowHover, useStoreState } from "../../../util/util";
import type { AxiosError } from "axios";
import axios from "axios";
import { useNotifications } from "../../../components/Notifications/NotificationsContext";
import { useAuthContext } from "../../../components/Auth";
import type { Getter } from "@tanstack/react-table";
import useSWR from "swr";
import { useCookies } from "react-cookie";
import { endpoints } from "../../../endpoints";
import type { Assets } from "../../../types/types.PIM";
import { NewAssetFormV2 } from "../ProductDetailPage/Assets/NewAssetFormV2";
import type { NewAssetFormSubmitValues } from "../ProductDetailPage/Assets/util";
import { TextMdBold } from "../../../components/Typography/TypographyV2";
import { AssetCategory } from "../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import { spacingTokens } from "../../../designTokens";

const TableWrapper = styled.div`
  table {
    td {
      max-width: 140px;
    }
  }
`;

export function AddDocumentToTransaction<ParentData>({
  existing_documents,
  products,
  transaction_type,
  transaction_type_id,
  fetchData,
  onComplete,
  onCancel,
}: {
  existing_documents: (TransactionDocument & { should_delete?: boolean })[];
  products: { name: string; id: string }[];
  transaction_type: "orders" | "quotes" | "sample-requests";
  transaction_type_id: string;
  fetchData: DataMutate<ParentData>;
  onComplete: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { storefront_id, slug, tenant_id } = useStoreState();
  const { notifyError, notifySuccess } = useNotifications();
  const { roleIsSomeKindOfBuyer } = useAuthContext();

  const [cookies] = useCookies([`preferred-language-${slug}`]);
  const preferred_language: string = cookies[`preferred-language-${slug}`];
  const [assetLanguages, setAssetLanguages] = useState<
    OptionType<SupportedLanguage>[]
  >([]);
  const [assetCategories, setAssetCategories] = useState<OptionType<string>[]>(
    []
  );

  const { data: languageResponse } = useSWR<{ languages: Language[] }>(
    endpoints.v1_storefronts_id_languages(storefront_id),
    {
      onError: () =>
        notifyError(t("could not load asset languages, Something went wrong.")),
    }
  );

  const { data: categoryResponse } = useSWR<AssetCategory>(
    `${endpoints.v2_tenants_id_pim_assets_categories(
      tenant_id
    )}?asset_type=document`,
    {
      onError: () =>
        notifyError(
          t("could not load document categories, Something went wrong.")
        ),
    }
  );

  const productOptions = useMemo(
    () => [
      ...products.reduce<OptionType<string>[]>(
        (acc, { name, id }) =>
          id ? [...acc, { label: name, value: id }] : acc,
        []
      ),
      { label: t("All products"), value: "all_products" },
    ],
    [products, t]
  );

  const columns = useMemo(() => {
    const deleteTransactionAsset = async (id: string) => {
      try {
        await axios.delete(
          `/v2/storefronts/${storefront_id}/${transaction_type}/${transaction_type_id}/documents/${id}`
        );
        await fetchData();
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage ?? t("Could not delete document. Something went wrong."),
          { error }
        );
      }
    };
    return [
      { accessorKey: "name", header: t("Document Name") },
      { accessorKey: "product_name", header: t("Product Name") },
      { accessorKey: "category", header: t("Category") },
      {
        accessorKey: "actions",
        header: "",
        align: "right",
        cell: ({
          getValue,
          row: { original },
        }: {
          getValue: Getter<{ id: string }>;
          row: { original: { should_delete: boolean } };
        }) => (
          <ButtonWithConfirmDialog
            Button={(props) => <DeleteButton {...props} />}
            testid="remove-transaction-document"
            disabled={!original.should_delete}
            datatip={
              !original.should_delete
                ? t("This document cannot be deleted.")
                : ""
            }
            datafor="remove-transaction-document-tooltip"
            handleConfirm={() => deleteTransactionAsset(getValue().id)}
            confirmMessage={t("Are you sure you want to delete this document?")}
          />
        ),
      },
    ];
  }, [
    fetchData,
    notifyError,
    storefront_id,
    t,
    transaction_type,
    transaction_type_id,
  ]);

  const onAddAssetData = async (data: NewAssetFormSubmitValues) => {
    const formData = new FormData();
    if (data.asset_source_mode === "external") {
      formData.append("external_url", data.external_asset?.url ?? "");
    } else {
      formData.append("file", data.file_type ?? "");
    }
    formData.append(
      "category_name",
      data.input_category_type.value === "other"
        ? data.custom_category_name ?? ""
        : data.input_category_type.label
    );
    formData.append("asset_name", data.asset_name ?? "");
    formData.append(
      "is_all_product",
      data.product_id?.value === "all_products" || !data.product_id?.value
        ? "true"
        : "false"
    );
    if (data.product_id?.value && data.product_id.value !== "all_products") {
      formData.append("product_id", data.product_id.value);
    }
    if (data.asset_host) {
      formData.append("asset_host_id", data.asset_host.value);
    }
    formData.append("language", data.input_lang_type?.value || "");
    try {
      await axios.put(
        `/v2/storefronts/${storefront_id}/${transaction_type}/${transaction_type_id}/documents`,
        formData
      );
      await fetchData();
      notifySuccess(t("Document added successfully."));
      onComplete();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage ?? t("Could not add document. Something went wrong."),
        { error }
      );
    }
  };

  const onAddExistingAssets = async ({
    selected_assets,
    selected_product,
    selected_language,
  }: {
    selected_assets: Assets[];
    selected_product?: OptionType<string>;
    selected_language?: OptionType<SupportedLanguage>;
  }) => {
    try {
      await axios.post(
        `/v2/storefronts/${storefront_id}/${transaction_type}/${transaction_type_id}/documents`,
        {
          asset_ids: selected_assets.map(({ id }) => id),
          ...(!selected_product || selected_product.value === "all_products"
            ? {}
            : { product_id: selected_product.value }),
          language: selected_language?.value ?? preferred_language,
          is_all_product:
            selected_product?.value === "all_products" || !selected_product,
        }
      );
      notifySuccess(t("Documents added successfully"));
      await fetchData();
      onComplete();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage ?? t("Could not add document. Something went wrong."),
        { error }
      );
    }
  };

  const tableData = existing_documents.map(
    ({ product_name, id, name, kind, should_delete }) => ({
      name,
      product_name: product_name || t("All"),
      category: kind,
      actions: { id },
      id,
      should_delete: should_delete ?? true,
    })
  );

  useEffect(() => {
    if (languageResponse) {
      const { languages } = languageResponse;
      setAssetLanguages(
        languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
    }
  }, [languageResponse]);

  useEffect(() => {
    if (categoryResponse) {
      const { asset_categories } = categoryResponse;
      setAssetCategories(
        asset_categories.map((category) => ({
          label: category.name,
          value: category.id,
        }))
      );
    }
  }, [categoryResponse]);

  return (
    <FormItems>
      {tableData.length > 0 && (
        <>
          <TextMdBold>{t("Existing Documents")}</TextMdBold>
          <TableWrapper style={{ marginTop: spacingTokens.spacing_md }}>
            <Table
              columns={columns}
              isLoading={false}
              error={undefined}
              data={tableData}
              rowHover={rowHover}
            />
          </TableWrapper>
        </>
      )}
      <TextMdBold>{t("New Document")}</TextMdBold>
      <NewAssetFormV2
        title="Documents"
        defaultAssetType="Documents"
        refreshParentData={fetchData}
        onComplete={onComplete}
        onCancel={onCancel}
        products={productOptions}
        uploadNewDocumentsOnly={roleIsSomeKindOfBuyer}
        hideAccessibility={true}
        showGeneratedAssets={true}
        existingAssetsLanguages={assetLanguages}
        default_asset_types={[{ label: "Document", value: "document" }]}
        default_categories={
          assetCategories.length
            ? assetCategories
            : [
                { label: "Technical Data Sheet (TDS)", value: "tds" },
                { label: "Safety Data Sheet (SDS)", value: "sds" },
                { label: "Other", value: "other" },
              ]
        }
        onAddAssetData={onAddAssetData}
        onAddExistingAssets={onAddExistingAssets}
      />
    </FormItems>
  );
}
